import { COMMERCE_EVENT_TYPES, type StoreDto, type WebhookEndpointDto } from '@cartly/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Modal,
  SelectField,
  Spinner,
  TextField,
  useConfirm,
  useToast,
} from '@cartly/ui';
import { Plus, Webhook } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../api/client';
import type { DeliveryRow, Page } from '../../api/types';
import { CopyField, LoadMore, StatusBadge } from '../../components/Common';
import { formatRelative } from '../../app/format';
import { useLoader, usePaged, useSubmit } from '../../app/hooks';
import { useActiveSession } from '../../app/session';

function EndpointModal({
  stores,
  onClose,
  onCreated,
}: {
  stores: StoreDto[];
  onClose: () => void;
  onCreated: (secret: string) => void;
}) {
  const submit = useSubmit();
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [storeId, setStoreId] = useState('');
  const [all, setAll] = useState(true);
  const [events, setEvents] = useState<Set<string>>(new Set(['order.created', 'order.paid']));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const res = await submit.run(() =>
      api.post<{ endpoint: WebhookEndpointDto; secret: string }>('/webhook-endpoints', {
        url,
        description,
        storeId: storeId || null,
        events: all ? ['*'] : [...events],
      }),
    );
    if (res) onCreated(res.secret);
  };

  return (
    <Modal
      open
      title="Add webhook endpoint"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="endpoint-form" loading={submit.pending}>
            Add endpoint
          </Button>
        </>
      }
    >
      <form id="endpoint-form" className="stack" onSubmit={onSubmit} noValidate>
        {submit.error && <Alert tone="critical">{submit.error}</Alert>}
        <TextField
          label="Endpoint URL"
          placeholder="https://erp.example.com/cartly"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          error={submit.fieldErrors['url']}
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <SelectField
          label="Store"
          value={storeId}
          options={[
            { value: '', label: 'All stores' },
            ...stores.map((s) => ({ value: s.id, label: s.name })),
          ]}
          onChange={(e) => setStoreId(e.target.value)}
        />
        <Checkbox
          label="Send every event type"
          checked={all}
          onChange={(e) => setAll(e.target.checked)}
        />
        {!all && (
          <fieldset className="permission-group event-picker">
            <legend className="permission-group__title">Event types</legend>
            {COMMERCE_EVENT_TYPES.map((t) => (
              <Checkbox
                key={t}
                label={<span className="mono">{t}</span>}
                checked={events.has(t)}
                onChange={() =>
                  setEvents((s) => {
                    const n = new Set(s);
                    if (n.has(t)) n.delete(t);
                    else n.add(t);
                    return n;
                  })
                }
              />
            ))}
          </fieldset>
        )}
        {submit.fieldErrors['events'] && (
          <span className="field__error">{submit.fieldErrors['events']}</span>
        )}
      </form>
    </Modal>
  );
}

function DeliveriesModal({
  endpoint,
  onClose,
}: {
  endpoint: WebhookEndpointDto;
  onClose: () => void;
}) {
  const toast = useToast();
  const list = usePaged(
    (cursor) =>
      api.get<Page<DeliveryRow>>(
        `/webhook-endpoints/${endpoint.id}/deliveries?limit=25${cursor ? `&cursor=${cursor}` : ''}`,
      ),
    endpoint.id,
  );
  const retry = async (id: string) => {
    try {
      await api.post(`/webhook-endpoints/deliveries/${id}/retry`);
      toast.success('Delivery queued');
      void list.reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <Modal open title="Recent deliveries" onClose={onClose}>
      {list.loading && list.items.length === 0 ? (
        <Spinner />
      ) : list.items.length === 0 ? (
        <p className="text-muted">No deliveries yet.</p>
      ) : (
        <div className="stack-sm">
          {list.items.map((d) => (
            <div key={d.id} className="delivery-row">
              <div>
                <div className="mono text-sm">{d.eventType}</div>
                <div className="table__secondary">
                  {d.responseStatus ? `HTTP ${d.responseStatus} · ` : ''}
                  {d.attempts} attempt{d.attempts === 1 ? '' : 's'} · {formatRelative(d.createdAt)}
                </div>
                {d.error && <div className="field__error">{d.error}</div>}
              </div>
              <div className="row">
                <StatusBadge status={d.status} />
                {(d.status === 'failed' || d.status === 'dead') && (
                  <Button size="sm" variant="ghost" onClick={() => void retry(d.id)}>
                    Retry
                  </Button>
                )}
              </div>
            </div>
          ))}
          {list.hasMore && (
            <LoadMore onClick={() => void list.loadMore()} loading={list.loadingMore} />
          )}
        </div>
      )}
    </Modal>
  );
}

export function WebhooksSettings() {
  const session = useActiveSession();
  const toast = useToast();
  const confirm = useConfirm();
  const [adding, setAdding] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [viewing, setViewing] = useState<WebhookEndpointDto | null>(null);
  const { data, error, loading, reload } = useLoader(async () => {
    const [endpoints, stores] = await Promise.all([
      api.get<{ items: WebhookEndpointDto[] }>('/webhook-endpoints'),
      api.get<{ items: StoreDto[] }>('/stores'),
    ]);
    return { endpoints: endpoints.items, stores: stores.items };
  }, session.activeTenant?.id);

  if (loading && !data) return <Spinner size="lg" label="Loading" />;
  if (error || !data) return <Alert tone="critical">{error ?? 'Failed to load'}</Alert>;

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
      await reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const storeName = (id: string | null) =>
    id ? (data.stores.find((s) => s.id === id)?.name ?? 'Removed store') : 'All stores';

  return (
    <>
      <Card
        title="Webhook endpoints"
        subtitle="Cartly360 sends signed events to your systems (ERP, warehouse, BI). Verify the Cartly-Signature header with the endpoint secret."
        actions={
          <Button variant="primary" icon={<Plus size={16} />} onClick={() => setAdding(true)}>
            Add endpoint
          </Button>
        }
        flush
      >
        {data.endpoints.length === 0 ? (
          <EmptyState icon={<Webhook size={22} />} title="No endpoints">
            Add an HTTPS endpoint to receive commerce events as they are processed.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Endpoint</th>
                  <th>Events</th>
                  <th>Status</th>
                  <th className="table__actions">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.endpoints.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <div className="table__primary mono break">{e.url}</div>
                      <div className="table__secondary">
                        {storeName(e.storeId)}
                        {e.description && ` · ${e.description}`}
                      </div>
                    </td>
                    <td className="text-sm">
                      {e.events.includes('*') ? 'All events' : `${e.events.length} types`}
                    </td>
                    <td>
                      {e.isActive ? (
                        <Badge tone="success">Active</Badge>
                      ) : (
                        <Badge>{e.disabledReason ?? 'Disabled'}</Badge>
                      )}
                    </td>
                    <td className="table__actions">
                      <div className="row row--end">
                        <Button size="sm" variant="ghost" onClick={() => setViewing(e)}>
                          Deliveries
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            void act(
                              () =>
                                api.patch(`/webhook-endpoints/${e.id}`, { isActive: !e.isActive }),
                              e.isActive ? 'Endpoint disabled' : 'Endpoint enabled',
                            )
                          }
                        >
                          {e.isActive ? 'Disable' : 'Enable'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            const ok = await confirm({
                              title: 'Rotate the signing secret?',
                              message:
                                'Update your receiver right away; the old secret stops verifying.',
                              confirmLabel: 'Rotate secret',
                            });
                            if (!ok) return;
                            try {
                              setSecret(
                                (
                                  await api.post<{ secret: string }>(
                                    `/webhook-endpoints/${e.id}/rotate-secret`,
                                  )
                                ).secret,
                              );
                            } catch (err) {
                              toast.error(errorMessage(err));
                            }
                          }}
                        >
                          Rotate secret
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            const ok = await confirm({
                              title: 'Delete this endpoint?',
                              message: 'Its delivery history is deleted with it.',
                              confirmLabel: 'Delete endpoint',
                              tone: 'danger',
                            });
                            if (ok) {
                              void act(
                                () => api.delete(`/webhook-endpoints/${e.id}`),
                                'Endpoint deleted',
                              );
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {adding && (
        <EndpointModal
          stores={data.stores}
          onClose={() => setAdding(false)}
          onCreated={(s) => {
            setAdding(false);
            setSecret(s);
            void reload();
          }}
        />
      )}
      {secret && (
        <Modal
          open
          title="Endpoint signing secret"
          onClose={() => setSecret(null)}
          footer={
            <Button variant="primary" onClick={() => setSecret(null)}>
              Done
            </Button>
          }
        >
          <div className="stack">
            <Alert tone="warning">
              Shown once. Store it with the receiving service and use it to verify signatures.
            </Alert>
            <CopyField label="Signing secret" value={secret} secret />
          </div>
        </Modal>
      )}
      {viewing && <DeliveriesModal endpoint={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}
